import React, { useState, useEffect } from 'react';
import { Wrench, Clock, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MaintenancePage = ({ maintenanceData }) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      window.location.reload();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Countdown timer if ETA is provided
  useEffect(() => {
    if (!maintenanceData?.eta) return;

    const updateCountdown = () => {
      try {
        const etaDate = new Date(maintenanceData.eta);
        const now = new Date();
        const diff = etaDate - now;

        if (diff <= 0) {
          setCountdown('Bald verfügbar');
          return;
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
          setCountdown(`ca. ${hours}h ${minutes}m`);
        } else {
          setCountdown(`ca. ${minutes}m`);
        }
      } catch (error) {
        setCountdown('');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [maintenanceData?.eta]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* Icon */}
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
            <Wrench className="w-8 h-8 text-emerald-600" />
          </div>
        </div>

        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">ivoliq</h1>
          <div className="w-12 h-1 bg-emerald-500 mx-auto rounded"></div>
        </div>

        {/* Main Message */}
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Wartungsarbeiten
        </h2>
        
        <p className="text-gray-600 mb-6 leading-relaxed">
          {maintenanceData?.message || 'Wir führen gerade Wartungsarbeiten durch, um Ihnen ein besseres Erlebnis zu bieten. Wir sind bald wieder da!'}
        </p>

        {/* ETA Display */}
        {maintenanceData?.eta && countdown && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-emerald-700">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Voraussichtlich verfügbar in: {countdown}</span>
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Aktualisiere...' : 'Status prüfen'}
        </button>

        {/* Auto-refresh notice */}
        <p className="text-xs text-gray-500 mt-4">
          Diese Seite wird automatisch alle 30 Sekunden aktualisiert.
        </p>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Bei Fragen erreichen Sie uns unter{' '}
            <a href="mailto:support@ivoliq.com" className="text-emerald-600 hover:text-emerald-700">
              support@ivoliq.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
